'use client';

import { ChevronRight, CornerDownLeft, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGlobalSearch } from '../../hooks/useGlobalSearch';
import { useRecentPagesStore } from '../../lib/recent-pages-store';
import { formatRelativeTime } from '../../lib/relative-time';
import { matchesLocalQuery, type SearchResult, searchResultValue } from '../../lib/search-result';
import type { CommandDestination, RecentEntry } from '../../types/command-palette';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../../ui/command';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../ui/dialog';
import { Kbd } from '../../ui/kbd';
import { getUserInitials } from '../../ui/user-avatar';
import { ContactTasksView } from './ContactTasksView';
import { RECENT_ENTITY_META } from './entity-meta';
import { type PaletteView, VIEW_PLACEHOLDER, viewChipLabel } from './palette-views';
import { SearchResultItem } from './SearchResultItem';

/** How many of each group the root view shows before offering a drill-in. */
const ROOT_RECENT_LIMIT = 5;
const ROOT_DESTINATION_LIMIT = 6;
/** Per-type result cap at root; the type-scoped view lifts it. */
const ROOT_RESULT_LIMIT = 5;
const SCOPED_RESULT_LIMIT = 50;

/** Stable identity so the store selector doesn't hand back a fresh array on every render. */
const EMPTY_ENTRIES: RecentEntry[] = [];

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The app's navigable surfaces, already workspace-scoped. Array order is meaningful: the
   * first ROOT_DESTINATION_LIMIT entries are the ones promoted to the root view.
   */
  destinations: CommandDestination[];
}

export function CommandPalette({ open, onOpenChange, destinations }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { orgId, workspaceId } = useParams<{ orgId: string; workspaceId: string }>();

  // Views form a stack so Escape returns wherever the user came from. The contact drill-in is
  // reachable from both the root results and a type-scoped list, so a fixed parent won't do.
  const [stack, setStack] = useState<PaletteView[]>([{ kind: 'root' }]);
  const [search, setSearch] = useState('');
  const [activeValue, setActiveValue] = useState('');

  const view = stack[stack.length - 1];
  const isRoot = stack.length === 1;

  const recent = useRecentPagesStore((s) =>
    workspaceId ? (s.byWorkspace[workspaceId] ?? EMPTY_ENTRIES) : EMPTY_ENTRIES
  );

  // Always reopen at the root with a clean query — carrying the previous session's drill-in
  // and search text over is disorienting for something driven by muscle memory.
  useEffect(() => {
    if (open) {
      setStack([{ kind: 'root' }]);
      setSearch('');
    }
  }, [open]);

  const push = (next: PaletteView) => {
    setStack((prev) => [...prev, next]);
    setSearch('');
  };

  const pop = () => {
    setStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
    setSearch('');
  };

  const isSearchableView = view.kind === 'root' || view.kind === 'results';
  const { results, isLoading, isActive } = useGlobalSearch(search, {
    types: view.kind === 'results' ? [view.entityType] : undefined,
    perTypeLimit: view.kind === 'results' ? SCOPED_RESULT_LIMIT : ROOT_RESULT_LIMIT,
    enabled: open && isSearchableView,
  });

  const contactResults = results.filter((r) => r.entityType === 'contact');
  const taskResults = results.filter((r) => r.entityType === 'task');

  /** cmdk lowercases item values internally, so the lookup key has to match. */
  const resultsByValue = useMemo(
    () => new Map(results.map((r) => [searchResultValue(r).toLowerCase(), r])),
    [results]
  );

  const go = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  const goToResult = (result: SearchResult) => {
    const base = `/org/${orgId}/workspace/${workspaceId}`;
    go(
      result.entityType === 'contact'
        ? `${base}/contacts/${result.id}`
        : `${base}/tasks/${result.id}`
    );
  };

  /** Tab / ArrowRight on a contact result opens everything attached to that contact. */
  const drillIntoActiveResult = () => {
    const active = resultsByValue.get(activeValue.toLowerCase());
    if (active?.entityType !== 'contact') return false;
    push({ kind: 'contact', contactId: active.id, contactName: active.title });
    return true;
  };

  const localQuery = search.trim();
  const filteredRecent = recent.filter((entry) =>
    matchesLocalQuery(
      [entry.label, entry.description, RECENT_ENTITY_META[entry.type].label],
      localQuery
    )
  );
  const filteredDestinations = destinations.filter((destination) =>
    matchesLocalQuery(
      [destination.title, destination.section, ...(destination.keywords ?? [])],
      localQuery
    )
  );

  const rootRecent = localQuery ? filteredRecent : filteredRecent.slice(0, ROOT_RECENT_LIMIT);
  const rootDestinations = localQuery
    ? filteredDestinations
    : filteredDestinations.slice(0, ROOT_DESTINATION_LIMIT);

  const destinationsBySection = useMemo(() => {
    const sections = new Map<string, CommandDestination[]>();
    for (const destination of filteredDestinations) {
      const bucket = sections.get(destination.section);
      if (bucket) bucket.push(destination);
      else sections.set(destination.section, [destination]);
    }
    return [...sections.entries()];
  }, [filteredDestinations]);

  const hasAnyRow =
    results.length > 0 ||
    (view.kind === 'root' && (rootRecent.length > 0 || rootDestinations.length > 0)) ||
    view.kind === 'contact';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="overflow-hidden p-0 sm:max-w-xl"
        // Escape inside a drill-in pops one view; only a press at the root closes the palette.
        // Radix dismisses on Escape via a document-level listener, so intercepting it here is
        // the only reliable way to keep the first press local — a React onKeyDown on the
        // Command below would fire too late to prevent the dismiss.
        onEscapeKeyDown={(event) => {
          if (!isRoot) {
            event.preventDefault();
            pop();
          }
        }}
      >
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        <DialogDescription className="sr-only">
          Search contacts and tasks, revisit recent pages, or jump to any destination.
        </DialogDescription>

        <Command
          // Remote results are already filtered and ranked by the server's FTS index, which
          // applies stemming that cmdk's substring filter would undo — a result the server
          // legitimately matched would vanish from the list. Local groups are filtered by
          // matchesLocalQuery instead.
          shouldFilter={false}
          value={activeValue}
          onValueChange={setActiveValue}
          onKeyDown={(event) => {
            // Backspace on an empty query is the conventional "pop the page" gesture.
            if (event.key === 'Backspace' && !search && !isRoot) {
              event.preventDefault();
              pop();
              return;
            }

            if (event.key === 'Tab' && !event.shiftKey) {
              if (drillIntoActiveResult()) event.preventDefault();
              return;
            }

            // ArrowRight only drills in from the end of the input, so it stays available for
            // ordinary caret movement while editing a query.
            if (event.key === 'ArrowRight') {
              const input = event.currentTarget.querySelector('input');
              const atEnd =
                input &&
                input.selectionStart === input.value.length &&
                input.selectionStart === input.selectionEnd;
              if (atEnd && drillIntoActiveResult()) event.preventDefault();
            }
          }}
        >
          {!isRoot && (
            <div className="flex items-center gap-2 border-b px-3 py-2">
              <span className="bg-secondary text-secondary-foreground max-w-[16rem] truncate rounded px-2 py-0.5 text-xs font-medium">
                {viewChipLabel(view)}
              </span>
              <span className="text-muted-foreground flex items-center gap-1 text-xs">
                <Kbd>esc</Kbd> to go back
              </span>
            </div>
          )}

          <CommandInput
            placeholder={VIEW_PLACEHOLDER[view.kind]}
            value={search}
            onValueChange={setSearch}
          />

          <CommandList className="max-h-[60vh]">
            {!hasAnyRow && !isLoading && (
              <CommandEmpty>
                <span className="text-muted-foreground text-sm">
                  {isActive || localQuery ? 'No results found.' : 'Start typing to search.'}
                </span>
              </CommandEmpty>
            )}

            {isLoading && (
              <div className="text-muted-foreground flex items-center gap-2 px-4 py-6 text-sm">
                <Search className="size-4 animate-pulse" />
                Searching…
              </div>
            )}

            {view.kind === 'contact' && (
              <ContactTasksView
                contactId={view.contactId}
                search={search}
                onSelectTask={(taskId) =>
                  go(`/org/${orgId}/workspace/${workspaceId}/tasks/${taskId}`)
                }
                onOpenContact={() =>
                  go(`/org/${orgId}/workspace/${workspaceId}/contacts/${view.contactId}`)
                }
              />
            )}

            {isSearchableView && contactResults.length > 0 && (
              <CommandGroup heading="Contacts">
                {contactResults.map((result) => (
                  <SearchResultItem
                    key={`contact-${result.id}`}
                    result={result}
                    onSelect={() => goToResult(result)}
                    onDrillIn={() =>
                      push({ kind: 'contact', contactId: result.id, contactName: result.title })
                    }
                  />
                ))}
                {view.kind === 'root' && contactResults.length === ROOT_RESULT_LIMIT && (
                  <BrowseItem
                    label="Search all contacts"
                    onSelect={() => push({ kind: 'results', entityType: 'contact' })}
                  />
                )}
              </CommandGroup>
            )}

            {isSearchableView && taskResults.length > 0 && (
              <CommandGroup heading="Tasks">
                {taskResults.map((result) => (
                  <SearchResultItem
                    key={`task-${result.id}`}
                    result={result}
                    onSelect={() => goToResult(result)}
                  />
                ))}
                {view.kind === 'root' && taskResults.length === ROOT_RESULT_LIMIT && (
                  <BrowseItem
                    label="Search all tasks"
                    onSelect={() => push({ kind: 'results', entityType: 'task' })}
                  />
                )}
              </CommandGroup>
            )}

            {view.kind === 'root' && results.length > 0 && rootRecent.length > 0 && (
              <CommandSeparator />
            )}

            {view.kind === 'root' && rootRecent.length > 0 && (
              <CommandGroup heading="Recent">
                {rootRecent.map((entry) => (
                  <RecentItem key={entry.key} entry={entry} onSelect={() => go(entry.path)} />
                ))}
                {!localQuery && filteredRecent.length > ROOT_RECENT_LIMIT && (
                  <BrowseItem
                    label="Browse all recent"
                    count={filteredRecent.length}
                    onSelect={() => push({ kind: 'recent' })}
                  />
                )}
              </CommandGroup>
            )}

            {view.kind === 'root' && rootDestinations.length > 0 && (
              <CommandGroup heading="Go to">
                {rootDestinations.map((destination) => (
                  <DestinationItem
                    key={destination.id}
                    destination={destination}
                    onSelect={() => go(destination.url)}
                  />
                ))}
                {!localQuery && filteredDestinations.length > ROOT_DESTINATION_LIMIT && (
                  <BrowseItem
                    label="Browse all destinations"
                    count={filteredDestinations.length}
                    onSelect={() => push({ kind: 'destinations' })}
                  />
                )}
              </CommandGroup>
            )}

            {/* Drill-ins drop the group heading — the chip above the input already names them. */}
            {view.kind === 'recent' && (
              <CommandGroup>
                {filteredRecent.map((entry) => (
                  <RecentItem key={entry.key} entry={entry} onSelect={() => go(entry.path)} />
                ))}
              </CommandGroup>
            )}

            {view.kind === 'destinations' &&
              destinationsBySection.map(([section, items]) => (
                <CommandGroup key={section} heading={section}>
                  {items.map((destination) => (
                    <DestinationItem
                      key={destination.id}
                      destination={destination}
                      onSelect={() => go(destination.url)}
                    />
                  ))}
                </CommandGroup>
              ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

function RecentItem({ entry, onSelect }: { entry: RecentEntry; onSelect: () => void }) {
  const meta = RECENT_ENTITY_META[entry.type];
  const Icon = meta.icon;

  return (
    <CommandItem
      value={`recent:${entry.key} ${entry.label} ${entry.description ?? ''} ${meta.label}`}
      onSelect={onSelect}
      className="gap-3"
    >
      {entry.avatarUrl ? (
        <Avatar className="size-6 shrink-0">
          <AvatarImage src={entry.avatarUrl} alt="" />
          <AvatarFallback className="text-[10px]">
            {getUserInitials({ fullName: entry.label })}
          </AvatarFallback>
        </Avatar>
      ) : (
        <span className="bg-muted text-muted-foreground flex size-6 shrink-0 items-center justify-center rounded">
          <Icon className="size-3.5" />
        </span>
      )}

      <span className="flex min-w-0 flex-col">
        <span className="truncate">{entry.label}</span>
        {entry.description && (
          <span className="text-muted-foreground truncate text-xs">{entry.description}</span>
        )}
      </span>

      <span className="text-muted-foreground ml-auto flex shrink-0 items-center gap-2 text-xs">
        <span>{meta.label}</span>
        <span className="tabular-nums opacity-70">{formatRelativeTime(entry.visitedAt)}</span>
      </span>
    </CommandItem>
  );
}

function DestinationItem({
  destination,
  onSelect,
}: {
  destination: CommandDestination;
  onSelect: () => void;
}) {
  const Icon = destination.icon;

  return (
    <CommandItem
      value={`goto:${destination.id} ${destination.title} ${destination.keywords?.join(' ') ?? ''} ${destination.section}`}
      onSelect={onSelect}
      className="gap-3"
    >
      <span className="bg-muted text-muted-foreground flex size-6 shrink-0 items-center justify-center rounded">
        <Icon className="size-3.5" />
      </span>
      <span className="truncate">{destination.title}</span>
    </CommandItem>
  );
}

function BrowseItem({
  label,
  count,
  onSelect,
}: {
  label: string;
  count?: number;
  onSelect: () => void;
}) {
  return (
    <CommandItem value={`browse:${label}`} onSelect={onSelect} className="gap-3">
      <span className="text-muted-foreground flex size-6 shrink-0 items-center justify-center">
        <ChevronRight className="size-3.5" />
      </span>
      <span className="text-muted-foreground">
        {label}
        {count !== undefined && <span className="opacity-60"> ({count})</span>}
      </span>
      <span className="text-muted-foreground ml-auto shrink-0">
        <CornerDownLeft className="size-3" />
      </span>
    </CommandItem>
  );
}
